// Pure helpers for the Reservations page's "Select a table" dropdown.
//
// GitHub issue #73 — the table dropdown was empty on the admin Reservations
// page.
//
// First attempt assumed the admin page had no location switcher and only
// skipped the fetch when `selectedLocationId` was undefined. That was wrong:
// `AdminLayout` renders a GLOBAL location switcher (see router.tsx) that sets
// `selectedLocationId` whenever the operator picks a specific branch. The
// table query was therefore still scoped to that branch, so the picker went
// empty as soon as a location was selected in the header (the common case in
// multi-location admin use) — the same root cause as the fixed issues #57 and
// #60 (a tenant-wide picker wrongly scoped to a globally-selected location).
//
// A reservation belongs to whatever table is chosen, not to the branch the
// header filter happens to point at, so the picker must ALWAYS list every
// active table in the tenant. `resolveReservationLocationId` then derives the
// new reservation's location from the chosen table.

import type { DiningTable } from '@/services/table.service';

/**
 * Query params for the table list shown in the reservation form's dropdown.
 *
 * The table picker is intentionally NOT scoped by the header's location
 * filter: a reservation is tied to a specific table (whose location is
 * resolved from the table itself), so the dropdown must offer every active
 * table in the tenant regardless of which branch — if any — is selected in
 * the global location switcher.
 */
export function buildTableListParams(): { limit: number } {
  return { limit: 200 };
}

/**
 * Resolve which location a new reservation belongs to. Prefer an explicitly
 * selected location; otherwise fall back to the chosen table's own location
 * so the reservation can still be created while viewing all locations.
 */
export function resolveReservationLocationId(
  selectedLocationId: string | undefined,
  selectedTable: Pick<DiningTable, 'location_id'> | undefined,
): string | undefined {
  return selectedLocationId ?? selectedTable?.location_id;
}
